import { useState, useEffect } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar, ArrowRight, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample news for demo
const sampleNews = [
  {
    id: "1",
    title: "Scadenza Modello 730: Tutte le Date da Ricordare",
    summary: "Ecco il calendario completo delle scadenze per la dichiarazione dei redditi 2026. Non perdere le date importanti per evitare sanzioni.",
    content: "La campagna fiscale 2026 è alle porte...",
    category: "fiscale",
    image_url: "https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800",
    created_at: new Date().toISOString(),
  },
  {
    id: "2",
    title: "Assegno Unico 2026: Novità e Importi Aggiornati",
    summary: "Sono stati pubblicati i nuovi importi dell'Assegno Unico Universale per il 2026. Scopri quanto spetta alla tua famiglia.",
    content: "L'INPS ha comunicato i nuovi importi...",
    category: "inps",
    image_url: "https://images.pexels.com/photos/3943716/pexels-photo-3943716.jpeg?auto=compress&cs=tinysrgb&w=800",
    created_at: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    id: "3",
    title: "ISEE Corrente: Quando e Come Richiederlo",
    summary: "Se hai avuto una variazione del reddito puoi richiedere l'ISEE corrente. Ti spieghiamo quando conviene e come fare.",
    content: "L'ISEE corrente è uno strumento che permette...",
    category: "isee",
    image_url: "https://images.pexels.com/photos/4386433/pexels-photo-4386433.jpeg?auto=compress&cs=tinysrgb&w=800",
    created_at: new Date(Date.now() - 172800000).toISOString(),
  },
  {
    id: "4",
    title: "Bonus Ristrutturazione 2026: Guida Completa",
    summary: "Tutte le novità sui bonus edilizi per il 2026. Cosa cambia per ristrutturazioni, ecobonus e bonus mobili.",
    content: "Il panorama dei bonus edilizi...",
    category: "bonus",
    image_url: "https://images.pexels.com/photos/5691622/pexels-photo-5691622.jpeg?auto=compress&cs=tinysrgb&w=800",
    created_at: new Date(Date.now() - 259200000).toISOString(),
  },
];

const categoryColors = {
  fiscale: "bg-blue-100 text-blue-800",
  inps: "bg-green-100 text-green-800",
  isee: "bg-purple-100 text-purple-800",
  bonus: "bg-orange-100 text-orange-800",
  generale: "bg-slate-100 text-slate-800",
};

const News = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/news`);
        if (response.data.length > 0) {
          setNews(response.data);
        } else {
          // Use sample news if none exist
          setNews(sampleNews);
        }
      } catch (error) {
        console.error("Error fetching news:", error);
        setNews(sampleNews);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  const categories = ["all", ...new Set(news.map(n => n.category))];
  const filteredNews = selectedCategory === "all" 
    ? news 
    : news.filter(n => n.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-400 mb-4 block">
              News & Aggiornamenti
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              Resta aggiornato
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Novità fiscali, scadenze importanti e aggiornamenti normativi per non perdere nessuna opportunità.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-semibold text-slate-500">Filtra per:</span>
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                className={`rounded-full capitalize ${
                  selectedCategory === cat ? "bg-blue-900" : ""
                }`}
                onClick={() => setSelectedCategory(cat)}
                data-testid={`filter-${cat}`}
              >
                {cat === "all" ? "Tutti" : cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredNews.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">Nessuna news trovata per questa categoria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((article, idx) => (
                <article
                  key={article.id}
                  className="news-card bg-white rounded-2xl overflow-hidden border border-slate-200 card-hover"
                  data-testid={`news-card-${idx}`}
                >
                  {article.image_url && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className={categoryColors[article.category] || categoryColors.generale}>
                        <Tag className="w-3 h-3 mr-1" />
                        {article.category}
                      </Badge>
                      <span className="text-sm text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(article.created_at), "d MMM yyyy", { locale: it })}
                      </span>
                    </div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-3 line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-slate-600 mb-4 line-clamp-3">
                      {article.summary}
                    </p>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-blue-900 font-semibold"
                      data-testid={`read-more-${idx}`}
                    >
                      Leggi tutto
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-blue-900 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              Non perdere nessuna scadenza
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Resta sempre aggiornato sulle novità fiscali e previdenziali. Seguici per ricevere tutti gli aggiornamenti.
            </p>
            <Link to="/contatti">
              <Button 
                size="lg"
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
                data-testid="news-cta-contatti"
              >
                Contattaci per Informazioni
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default News;
